/*
 *   File : add-basket.js
 *   Author : https://evoqins.com
 *   Description : add basket component
 *   Integrations : Backend API integrated
 *   Version : 1.0.2
 */

import React, { useEffect, useState } from "react";
import { CustomFileUpload, Input } from "../../Components/FormElements";
import { AboutSections, Funds } from "./Helper";
import { PrimaryButton } from "../../Components/Buttons";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  setNavLink,
  setPageTitle,
  setSearchEnabled,
} from "../../Redux/Ui/ui.slice";
import { APIService } from "../../Service";
import { toast } from "react-toastify";

export default function AddBasket() {
  const navigate = useNavigate();

  //Redux title and searchbar setting
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setPageTitle(["Manage Basket", "Add Basket"]));
    dispatch(setNavLink(["/manage-basket"]));
    dispatch(setSearchEnabled(false));
  }, []);

  /* --------------------- STATES --------------------- */

  // Basic info
  const [basketName, setBasketName] = useState("");
  const [basketNameError, setBasketNameError] = useState("");

  // Best For (numbers only — stored as year count)
  const [bestFor, setBestFor] = useState("");
  const [bestForError, setBestForError] = useState("");

  // Risk (text only)
  const [risk, setRisk] = useState("");
  const [riskError, setRiskError] = useState("");

  // Logo
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [logoError, setLogoError] = useState("");

  const handleLogoUploadComplete = (url) => {
    setLogoUrl(url);
    setLogoError("");
  };

  // About items
  const [aboutItems, setAboutItems] = useState([]);
  const [aboutErrorState, setAboutErrorState] = useState({
    items: [],
  });

  const handleAboutItemsChange = (list) => {
    setAboutItems(list);
  };

  // SIP / Lumpsum
  const [minSip, setMinSip] = useState("");
  const [minSipError, setMinSipError] = useState("");

  const [minLumpsum, setMinLumpsum] = useState("");
  const [minLumpsumError, setMinLumpsumError] = useState("");

  // Funds
  const [fundGroups, setFundGroups] = useState([]);
  const [fundsErrorState, setFundsErrorState] = useState({
    general: "",
    items: [],
  });

  const handleFundsSubmit = (groups) => {
    setFundGroups(groups);
  };

  // Submit loading
  const [submitLoading, setSubmitLoading] = useState(false);

  /* --------------------- FORM VALIDATION --------------------- */

  const validateForm = () => {
    let valid = true;

    // Basket Name
    if (!basketName.trim()) {
      setBasketNameError("Basket name is required");
      valid = false;
    } else if (basketName.trim().length < 3) {
      setBasketNameError("Basket name must be at least 3 characters");
      valid = false;
    }

    // Best For — must be a positive integer (year count)
    if (!bestFor.toString().trim()) {
      setBestForError("Best For is required");
      valid = false;
    } else if (isNaN(bestFor) || parseInt(bestFor) <= 0) {
      setBestForError("Best For must be a valid positive number");
      valid = false;
    } else if (!Number.isInteger(Number(bestFor))) {
      setBestForError("Best For must be a whole number (e.g. 5)");
      valid = false;
    }

    // Risk — must be non-empty text, no digits allowed
    if (!risk.trim()) {
      setRiskError("Risk is required");
      valid = false;
    } else if (/\d/.test(risk.trim())) {
      setRiskError("Risk must contain only text (e.g. Low, Medium, High)");
      valid = false;
    } else if (risk.trim().length < 2) {
      setRiskError("Risk must be at least 2 characters");
      valid = false;
    }

    // Logo
    if (!logoFile && !logoUrl) {
      setLogoError("Please upload a basket logo");
      valid = false;
    }

    // About Section validation
    if (!Array.isArray(aboutItems) || aboutItems.length === 0) {
      setAboutErrorState({
        general: "Add at least one about section",
        items: [],
      });
      valid = false;
    } else {
      const itemsErrors = aboutItems.map(() => ({
        textError: "",
        fileError: "",
      }));

      aboutItems.forEach((it, idx) => {
        if (!it.text || !it.text.trim()) {
          itemsErrors[idx].textError = "About description is required";
          valid = false;
        }
        if (!it.file && !it.fileUrl) {
          itemsErrors[idx].fileError = "Please upload an image";
          valid = false;
        }
      });

      setAboutErrorState({
        general: valid ? "" : "Please fix the highlighted about section errors",
        items: itemsErrors,
      });
    }

    // SIP
    if (!minSip.toString().trim()) {
      setMinSipError("Min SIP is required");
      valid = false;
    } else if (isNaN(minSip) || parseInt(minSip) <= 0) {
      setMinSipError("Min SIP must be a valid positive number");
      valid = false;
    } else if (parseInt(minSip) < 100) {
      setMinSipError("Min SIP must be at least ₹100");
      valid = false;
    }

    // Lumpsum
    if (!minLumpsum.toString().trim()) {
      setMinLumpsumError("Min Lumpsum is required");
      valid = false;
    } else if (isNaN(minLumpsum) || parseInt(minLumpsum) <= 0) {
      setMinLumpsumError("Min Lumpsum must be a valid positive number");
      valid = false;
    } else if (parseInt(minLumpsum) < 500) {
      setMinLumpsumError("Min Lumpsum must be at least ₹500");
      valid = false;
    } else if (parseInt(minLumpsum) < parseInt(minSip)) {
      setMinLumpsumError(
        "Min Lumpsum should be greater than or equal to Min SIP",
      );
      valid = false;
    }

    // FUNDS VALIDATION
    {
      // --- 1️⃣ No fund groups added ---
      if (fundGroups.length === 0) {
        setFundsErrorState({
          general: "Add at least one fund allocation group",
          items: [
            {
              fundError: "Select a fund",
              fundTypeError: "Select fund type",
              allocationError: "Enter valid allocation",
            },
          ],
        });
        valid = false;
        return valid; // stop here
      }

      // --- 2️⃣ Allocation total must be exactly 100 ---
      const totalAllocation = fundGroups.reduce(
        (sum, g) => sum + Number(g.allocation || 0),
        0,
      );

      if (totalAllocation !== 100) {
        setFundsErrorState({
          general: "Total allocation must be exactly 100%.",
          items: fundGroups.map(() => ({
            fundError: "",
            fundTypeError: "",
            allocationError: "",
          })),
        });
        valid = false;
      }

      // --- 3️⃣ Per-row validation ---
      const itemsErrors = fundGroups.map((group) => ({
        fundError: !group.selectedFund ? "Select a fund" : "",
        fundTypeError: !group.fundType ? "Select fund type" : "",
        allocationError:
          !group.allocation || Number(group.allocation) <= 0
            ? "Enter valid allocation"
            : "",
      }));

      const hasItemErrors = itemsErrors.some(
        (i) => i.fundError || i.fundTypeError || i.allocationError,
      );

      if (hasItemErrors) {
        setFundsErrorState({
          general: "All fund groups must be completely filled",
          items: itemsErrors,
        });
        valid = false;
      }

      // --- 4️⃣ No errors ---
      if (totalAllocation === 100 && !hasItemErrors) {
        setFundsErrorState({
          general: "",
          items: fundGroups.map(() => ({
            fundError: "",
            fundTypeError: "",
            allocationError: "",
          })),
        });
      }
    }

    return valid;
  };

  /* --------------------- FINAL SUBMIT --------------------- */

  const handleSubmit = () => {
    if (!validateForm()) return;

    setSubmitLoading(true);

    const payload = {
      basket_style_id: null,
      name: basketName.trim(),
      image: logoUrl,
      best_for: `${parseInt(bestFor)} year`,
      risk: risk.trim(),
      about_sections: aboutItems.map((item) => ({
        title: item.text,
        image: item.fileUrl || "",
      })),
      min_sip: parseInt(minSip),
      min_lumpsum: parseInt(minLumpsum),
      funds: fundGroups.map((fund) => ({
        fund_id: fund.selectedFund?.value || fund.selectedFund?.fund_id,
        fund_type: `${fund.fundType?.value}` || `${fund.fundType}`,
        allocation_percentage: parseFloat(fund.allocation),
      })),
    };

    console.log("FINAL PAYLOAD:", payload);

    APIService(true, "manage-basket/upsert", payload)
      .then((res) => {
        if (res.status_code === 200) {
          toast.success(res.message || "Basket created successfully");
          navigate("/manage-basket");
        } else {
          toast.error(res.message || "Failed to create basket");
          console.error("Error creating basket:", res.message);
        }
      })
      .catch((error) => {
        toast.error(error.message || "Failed to create basket");
        console.error("Error creating basket:", error);
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  /* --------------------- UI --------------------- */

  return (
    <div className="row">
      <div className="col-12">
        <div className="d-flex w-100 flex-column e-glass-card bg-opacity-43 shadow-none rounded-24 p-24">
          <div className="d-flex w-100 flex-column e-glass-card bg-opacity-43 shadow-none rounded-16 p-3">
            <div className="row">
              <div className="col-lg-6 col-12">
                <h4 className="fw-500 fs-18 lh-20 text-eerie-black mb-0 pb-3">
                  Add Basket
                </h4>

                {/* Basket Name */}
                <Input
                  id="basket_name"
                  type="text"
                  label="Basket Name"
                  placeholder="Enter Basket Name"
                  className="w-100 px-3 py-10"
                  require={true}
                  value={basketName}
                  error={basketNameError}
                  autoFocus={true}
                  handleChange={(v) => {
                    if (/\d/.test(v)) return;
                    setBasketName(v);
                    setBasketNameError("");
                  }}
                />

                {/* Best For — numbers only, appended with "year" in payload */}
                <div className="d-flex w-100 position-relative">
                  <Input
                    id="best_for"
                    type="number"
                    label="Best For"
                    placeholder="Enter number of years"
                    className="w-100 px-3 py-10 pe-5"
                    require={true}
                    value={bestFor}
                    error={bestForError}
                    handleChange={(v) => {
                      // Allow only positive integers
                      const numValue = v.replace(/[^0-9]/g, "");
                      setBestFor(numValue);
                      setBestForError("");
                    }}
                  />
                  <div className="position-absolute end-0 pt-4 mt-3 pe-3 w-max h-100">
                    <p className="mb-0 e-primary-font-rubik fs-14 bg-white ps-2 fw-400">
                      Years
                    </p>
                  </div>
                </div>

                {/* Risk — text only */}
                <Input
                  id="risk"
                  type="text"
                  label="Risk"
                  placeholder="Enter Risk (e.g. Low, Medium, High)"
                  className="w-100 px-3 py-10"
                  require={true}
                  value={risk}
                  error={riskError}
                  handleChange={(v) => {
                    // Strip any digits the user may try to type
                    const textOnly = v.replace(/[0-9]/g, "");
                    setRisk(textOnly);
                    setRiskError("");
                  }}
                />

                {/* Basket Logo */}
                <div className="d-flex flex-column row-gap-2 pb-2">
                  <p className="mb-0 fw-400 fs-14">
                    Upload logo <span className="text-cg-red">*</span>
                  </p>

                  <CustomFileUpload
                    file={logoFile || logoUrl}
                    type={2}
                    fileSize={3}
                    handleClear={() => {
                      setLogoFile(null);
                      setLogoUrl("");
                    }}
                    error={logoError}
                    handleChange={(file) => {
                      setLogoFile(file);
                      setLogoError("");
                    }}
                    onUploadComplete={handleLogoUploadComplete}
                  />
                  {logoError && (
                    <div className="text-cg-red fs-12 mt-1">{logoError}</div>
                  )}
                </div>

                {/* About Section */}
                <AboutSections
                  data={aboutItems}
                  onChange={handleAboutItemsChange}
                  maxItems={5}
                  errorState={aboutErrorState}
                  setErrorState={setAboutErrorState}
                />
                <h4 className="fw-500 fs-18 text-eerie-black pt-24 mb-0 pb-3">
                  Min SIP & Lumpsum
                </h4>

                {/* Min SIP */}
                <div className="position-relative mb-3">
                  <Input
                    id="min_sip"
                    type="number"
                    label="Min SIP"
                    placeholder="Enter Min SIP"
                    require={true}
                    value={minSip}
                    error={minSipError}
                    className="w-100 ps-4 pe-3 py-10"
                    handleChange={(v) => {
                      const numValue = v.replace(/[^0-9]/g, "");
                      setMinSip(numValue);
                      setMinSipError("");
                    }}
                  />
                  <span
                    style={{ top: "41%", left: "2%" }}
                    className="position-absolute"
                  >
                    ₹
                  </span>
                </div>

                {/* Min Lumpsum */}
                <div className="position-relative">
                  <Input
                    id="min_lumpsum"
                    type="number"
                    label="Min Lumpsum"
                    placeholder="Enter Min Lumpsum"
                    require={true}
                    className="w-100 ps-4 pe-3 py-10"
                    value={minLumpsum}
                    error={minLumpsumError}
                    handleChange={(v) => {
                      const numValue = v.replace(/[^0-9]/g, "");
                      setMinLumpsum(numValue);
                      setMinLumpsumError("");
                    }}
                  />
                  <span
                    style={{ top: "41%", left: "2%" }}
                    className="position-absolute"
                  >
                    ₹
                  </span>
                </div>
              </div>

              {/* Funds */}
              <div className="col-lg-10 col-12">
                <Funds
                  data={fundGroups}
                  onSubmit={handleFundsSubmit}
                  errorState={fundsErrorState}
                  setErrorState={setFundsErrorState}
                />
              </div>

              {/* Action Buttons */}
              <div className="row">
                <div className="offset-lg-4 col-lg-6 col-12">
                  <div className="row row-cols-2 pt-24">
                    <div className="col">
                      <PrimaryButton
                        name="Cancel"
                        ClassName="rounded-pill bg-transparent border-oxford-blue py-3 text-oxford-blue w-100 fs-16 fw-500"
                        onPress={() => navigate("/manage-basket")}
                      />
                    </div>
                    <div className="col">
                      <PrimaryButton
                        name={submitLoading ? "Processing..." : "Create"}
                        ClassName="rounded-pill bg-oxford-blue py-3 text-white w-100 fs-16 fw-500"
                        onPress={handleSubmit}
                        disabled={submitLoading}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
